/**
 * FilterManager - Reusable multi-filter utility with show more/less functionality
 *
 * Used by: vacancy_list.blade.php, and any component with filterable lists
 */

export interface FilterConfig {
    readonly containerSelector: string;
    readonly itemSelector: string;
    readonly filterSelectors: string[];
    readonly loadMoreSelector?: string;
    readonly noResultsSelector?: string;
    readonly initialLimit?: number;
    readonly visibleClass?: string;
    readonly showAllClass?: string;
    readonly showMoreText?: string;
    readonly showLessText?: string;
}

interface FilterElements {
    readonly container: HTMLElement;
    readonly list: HTMLElement;
    readonly items: NodeListOf<Element>;
    readonly filters: HTMLSelectElement[];
    readonly loadMore: HTMLElement | null;
    readonly noResults: HTMLElement | null;
}

export class FilterManager {
    private readonly config: Required<FilterConfig>;
    private readonly elements: FilterElements;

    constructor(config: FilterConfig) {
        this.config = {
            loadMoreSelector: '.show-more',
            noResultsSelector: '.no-results',
            initialLimit: 5,
            visibleClass: 'visible',
            showAllClass: 'show-all',
            showMoreText: 'Show more',
            showLessText: 'Show less',
            ...config
        };

        const container = document.querySelector<HTMLElement>(this.config.containerSelector);

        if (!container) {
            throw new Error(`FilterManager: Container not found: ${this.config.containerSelector}`);
        }

        const list = container.querySelector<HTMLElement>('.list-container') || container;

        this.elements = {
            container,
            list,
            items: container.querySelectorAll(this.config.itemSelector),
            filters: this.config.filterSelectors.map((selector) => {
                return container.querySelector<HTMLSelectElement>(selector);
            }).filter((el): el is HTMLSelectElement => el !== null),
            loadMore: container.querySelector<HTMLElement>(this.config.loadMoreSelector),
            noResults: container.querySelector<HTMLElement>(this.config.noResultsSelector)
        };

        this.init();
    }

    private init(): void {
        this.bindFilterEvents();
        this.bindLoadMoreEvent();
        this.applyFilters();
    }

    private bindFilterEvents(): void {
        this.elements.filters.forEach((filter) => {
            filter.addEventListener('change', () => {
                this.elements.list.classList.remove(this.config.showAllClass);
                this.applyFilters();
            });
        });
    }

    private bindLoadMoreEvent(): void {
        if (this.elements.loadMore === null) {
            return;
        }

        this.elements.loadMore.addEventListener('click', () => {
            this.elements.list.classList.toggle(this.config.showAllClass);
            this.updateVisibility();
        });
    }

    private getSelectedValues(select: HTMLSelectElement): string[] {
        return Array.from(select.selectedOptions).map((opt) => opt.value);
    }

    private applyFilters(): void {
        const filterValues = this.elements.filters.map((filter) => {
            return {
                name: filter.name.replace('Select', '').toLowerCase(),
                values: this.getSelectedValues(filter)
            };
        });

        this.elements.items.forEach((item) => {
            const element = item as HTMLElement;
            element.classList.remove(this.config.visibleClass);

            const matches = filterValues.every((filter) => {
                if (filter.values.length === 0) {
                    return true;
                }

                const itemValue = element.dataset[filter.name];
                return itemValue !== undefined && filter.values.includes(itemValue);
            });

            if (matches) {
                element.classList.add(this.config.visibleClass);
            }
        });

        this.updateVisibility();
    }

    private updateVisibility(): void {
        const visibleItems = this.elements.list.querySelectorAll(`.${this.config.visibleClass}`);
        const isShowAll = this.elements.list.classList.contains(this.config.showAllClass);

        this.elements.items.forEach((item) => {
            (item as HTMLElement).style.display = 'none';
        });

        if (isShowAll) {
            visibleItems.forEach((item) => {
                (item as HTMLElement).style.display = '';
            });
        } else {
            Array.from(visibleItems)
                .slice(0, this.config.initialLimit)
                .forEach((item) => {
                    (item as HTMLElement).style.display = '';
                });
        }

        this.updateLoadMoreButton(visibleItems.length, isShowAll);
        this.updateNoResults(visibleItems.length);
    }

    private updateLoadMoreButton(visibleCount: number, isShowAll: boolean): void {
        if (this.elements.loadMore === null) {
            return;
        }

        if (visibleCount > this.config.initialLimit) {
            this.elements.loadMore.style.display = '';
            const textElement = this.elements.loadMore.querySelector('span');
            const text = isShowAll ? this.config.showLessText : this.config.showMoreText;

            if (textElement) {
                textElement.textContent = text;
            }

            if (isShowAll) {
                this.elements.loadMore.classList.add('show-less');
            } else {
                this.elements.loadMore.classList.remove('show-less');
            }
        } else {
            this.elements.loadMore.style.display = 'none';
        }
    }

    private updateNoResults(visibleCount: number): void {
        if (this.elements.noResults === null) {
            return;
        }

        if (visibleCount === 0) {
            this.elements.list.classList.add('d-none');
            this.elements.noResults.classList.remove('d-none');
        } else {
            this.elements.list.classList.remove('d-none');
            this.elements.noResults.classList.add('d-none');
        }
    }

    public refresh(): void {
        this.applyFilters();
    }

    public reset(): void {
        this.elements.filters.forEach((filter) => {
            filter.selectedIndex = -1;

            if ((filter as HTMLSelectElement & { _multiSelect?: { clearSelection: () => void } })._multiSelect) {
                (filter as HTMLSelectElement & { _multiSelect: { clearSelection: () => void } })._multiSelect.clearSelection();
            }
        });

        this.elements.list.classList.remove(this.config.showAllClass);
        this.applyFilters();
    }
}

export default FilterManager;